import React, { useState } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    FormField,
    Input,
    Textarea,
    Select,
    Container,
    Header,
    ContentLayout,
    Box,
    Alert,
    StatusIndicator
} from '@amzn/awsui-components-console';
import { useAtomValue } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { profileCreationSchema, validateWithSchema } from '../../utils/validationSchemas';
import { useUserLdapGroups } from '../../hooks/useUserLdapGroups';
import './ProfileCreation.scss';

interface ProfileCreationProps {
    onCancel: () => void;
    onSubmit: (profileData: any) => void;
}

const ProfileCreation: React.FC<ProfileCreationProps> = ({ onCancel, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        ldapGroup: null as { label: string; value: string } | null
    });

    const [errors, setErrors] = useState<any>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);
    

    // Fetch user's LDAP groups from API
    const { ldapGroups, loading: ldapLoading, error: ldapError, refresh: refreshLdapGroups } = useUserLdapGroups();

    const validateForm = () => {
        // Prepare data for validation
        const dataToValidate = {
            profileName: formData.name.trim(),
            ldap: formData.ldapGroup?.value || '',
            description: formData.description.trim()
        };

        // Validate using Zod schema
        const validationResult = validateWithSchema(profileCreationSchema, dataToValidate);
        
        if (!validationResult.success) {
            // Map Zod errors to form field names
            const mappedErrors: any = {};
            Object.entries(validationResult.errors).forEach(([key, value]) => {
                // Map schema field names to form field names
                if (key === 'profileName') {
                    mappedErrors.name = value;
                } else if (key === 'ldap') {
                    mappedErrors.ldapGroup = value;
                } else {
                    mappedErrors[key] = value;
                }
            });
            
            setErrors(mappedErrors);
            return false;
        }
        
        setErrors({});
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setApiError(null);

        try {
            
            // Prepare the API request according to the documentation
            const request = {
                profileName: formData.name.trim(),
                ldap: formData.ldapGroup?.value || '',
                description: formData.description.trim()
                // userId is captured from authentication context by the backend
            };

            // Call the CreateProfile API
            const response = await makeApiCall(API_PATHS.PROFILES.CREATE, 'POST', request);

            // Call the parent's onSubmit with the created profile data
            // API returns { profile: { profileId, profileName, ... } }
            const profileData = response.profile || response;
            onSubmit({
                profileId: profileData.profileId,
                profileName: profileData.profileName,
                description: profileData.description,
                ldap: profileData.ldap,
                createdBy: profileData.createdBy,
                createdAt: profileData.createdAt
            });

            // Reset form
            setFormData({
                name: '',
                description: '',
                ldapGroup: null
            });
            setErrors({});
            
        } catch (error: any) {
            console.error('Failed to create profile:', error);
            
            // Handle specific error cases
            if (error.message?.includes('ProfileAlreadyExistsException')) {
                setApiError('A profile with this ID already exists. Please try again.');
            } else if (error.message?.includes('InvalidInputException')) {
                setApiError('Invalid input. Please check your profile details and try again.');
            } else {
                setApiError(error.message || 'Failed to create profile. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ContentLayout
            defaultPadding={false}
            headerVariant="high-contrast"
            header={
                <Box padding={{ horizontal: "xxxl", vertical: "l" }}>
                    <SpaceBetween size="m" direction="vertical">
                        <BreadcrumbGroup
                            items={[
                                { text: "Home", href: "/" },
                                { text: "Create Profile", href: "#" }
                            ]}
                            ariaLabel="Breadcrumbs"
                        />
                        <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                Create Profile
                        </Box>
                        <Box fontSize="body-m" color="inherit">
                                Create profile which can be used by team
                        </Box>
                    </SpaceBetween>
                </Box>
            }
        >
            <Box padding={{ horizontal: "xl", vertical: "xl" }}>
                <SpaceBetween direction="vertical" size="l">
                    {apiError && (
                        <Alert
                            type="error"
                            dismissible
                            onDismiss={() => setApiError(null)}
                            header="Error creating profile"
                        >
                            {apiError}
                        </Alert>
                    )}
                    
                    <Container
                        header={
                            <Header variant="h2">
                                Profile details
                            </Header>
                        }
                    >
                        <SpaceBetween direction="vertical" size="l">
                            <FormField
                                label="Name"
                                errorText={errors.name}
                                constraintText={`${formData.name.length}/255 characters`}
                                stretch={true}
                            >
                                <Input
                                    value={formData.name}
                                    onChange={({ detail }) => {
                                        setFormData({ ...formData, name: detail.value });
                                        // Clear error when user starts typing
                                        if (errors.name) {
                                            setErrors({ ...errors, name: undefined });
                                        }
                                    }}
                                    placeholder="Profile name eg: US Accounting"
                                    ariaLabel="Profile name"
                                />
                            </FormField>

                            <FormField
                                label="Description"
                                errorText={errors.description}
                                constraintText={`${formData.description.length}/1000 characters`}
                                stretch={true}
                            >
                                <Textarea
                                    value={formData.description}
                                    onChange={({ detail }) => {
                                        setFormData({ ...formData, description: detail.value });
                                        // Clear error when user starts typing
                                        if (errors.description) {
                                            setErrors({ ...errors, description: undefined });
                                        }
                                    }}
                                    placeholder="Profile description eg: For US Accounting Use Case"
                                    rows={3}
                                    ariaLabel="Profile description"
                                />
                            </FormField>

                            <FormField
                                label="Choose LDAP"
                                description="Member of the selected LDAP group will have access to this profile"
                                errorText={errors.ldapGroup}
                                stretch={true}
                                secondaryControl={
                                    ldapError && (
                                        <Button
                                            variant="icon"
                                            iconName="refresh"
                                            onClick={refreshLdapGroups}
                                            ariaLabel="Retry loading LDAP groups"
                                            loading={ldapLoading}
                                        />
                                    )
                                }
                            >
                                {ldapError && (
                                    <Box margin={{ bottom: "s" }}>
                                        <Alert
                                            type="error"
                                            dismissible
                                            onDismiss={() => {}}
                                        >
                                            {ldapError}
                                        </Alert>
                                    </Box>
                                )}
                                <Select
                                    selectedOption={formData.ldapGroup}
                                    onChange={({ detail }) => {
                                        const selected = detail.selectedOption;
                                        if (selected && selected.value) {
                                            setFormData({ 
                                                ...formData, 
                                                ldapGroup: { 
                                                    label: selected.label || selected.value,
                                                    value: selected.value 
                                                } 
                                            });
                                            setErrors({ ...errors, ldapGroup: undefined });
                                        }
                                    }}
                                    options={ldapGroups}
                                    placeholder={ldapLoading ? "Loading LDAP groups..." : "Select an LDAP group"}
                                    empty={ldapLoading ? "Loading..." : "No LDAP groups available"}
                                    ariaLabel="LDAP group selection"
                                    disabled={ldapLoading || !!ldapError}
                                    statusType={ldapLoading ? "loading" : "finished"}
                                    filteringType="auto"
                                    selectedAriaLabel="Selected"
                                />
                            </FormField>
                        </SpaceBetween>

                    <Box float="right" margin={{ top: "xl" }}>
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button 
                                onClick={onCancel}
                                variant="link"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleSubmit}
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Submit'}
                            </Button>
                            <Box padding={{ horizontal: "xxxl", vertical: "l", bottom: "xl" }}></Box>
                        </SpaceBetween>
                    </Box>
                </Container>
                </SpaceBetween>
            </Box>
        </ContentLayout>
    );
};

export default ProfileCreation;